/**
 * Staged zip that outlives the Sheet AgentDock.
 * Keyed by `${org}/${project}` so project A never leaks into B.
 * Cleared on successful send — not only by the chip ×.
 */

import { thinkFilePart, type ThinkFilePart } from "./think-drop.ts";

export type StagedThinkZip = {
  org: string;
  project: string;
  filename: string;
  artifactId: string;
  pathCount: number;
};

const stagedByProject = new Map<string, StagedThinkZip>();
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

export function thinkStagedZipKey(org: string, project: string): string {
  return `${org}/${project}`;
}

export function getStagedThinkZip(org: string, project: string): StagedThinkZip | null {
  return stagedByProject.get(thinkStagedZipKey(org, project)) ?? null;
}

export function setStagedThinkZip(next: StagedThinkZip): void {
  stagedByProject.set(thinkStagedZipKey(next.org, next.project), next);
  emit();
}

export function clearStagedThinkZip(org: string, project: string): void {
  const key = thinkStagedZipKey(org, project);
  if (!stagedByProject.has(key)) return;
  stagedByProject.delete(key);
  emit();
}

/** Test helper — wipe every project slot. */
export function resetStagedThinkZip(): void {
  if (stagedByProject.size === 0) return;
  stagedByProject.clear();
  emit();
}

export function subscribeStagedThinkZip(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Attachments win; otherwise this project's staged zip. Foreign `kept` is ignored. */
export function takeThinkSendFileParts(
  attachmentParts: ThinkFilePart[],
  stagedZip: StagedThinkZip | null,
  org?: string,
  project?: string,
): ThinkFilePart[] {
  if (attachmentParts.length > 0) return attachmentParts;
  if (!stagedZip) return [];
  if (
    org &&
    project &&
    thinkStagedZipKey(stagedZip.org, stagedZip.project) !== thinkStagedZipKey(org, project)
  ) {
    return [];
  }
  return [thinkFilePart(stagedZip.artifactId, stagedZip.filename)];
}
